import { useState } from 'react';

import { Icon, IconButton } from '@atom/mui';
import {
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
  SearchIcon,
} from '@atom/mui/icons';
import { MapSearch } from '@atom/types/map';

import { MapEditingSlice } from '../drawing/drawingTypes';

import MapEditList from './MapEditList';
import SearchAutocomplete from './SearchAutocomplete';
import useMapSearch from './useMapSearch';

import classes from './toolbar.module.css';

type EditableSearchBoxProps = {
  editing: MapEditingSlice;
  initialSearchTerm?: string;
  onSearchChange?: (search: MapSearch) => void;
  compactToolbars?: boolean;
};

const EditableSearchBox = ({
  editing,
  initialSearchTerm,
  onSearchChange,
  compactToolbars = false,
}: EditableSearchBoxProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(true);
  const search = useMapSearch({ initialSearchTerm, onSearchChange });

  const isEditing = editing.isEditing;

  const startAdornment = isEditing ? (
    <Icon icon={SearchIcon} />
  ) : (
    <IconButton
      size="small"
      data-cy="mapEditCollapse"
      onClick={() => setOpen(previous => !previous)}
    >
      <Icon icon={open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon} />
    </IconButton>
  );

  return (
    <>
      <div
        className={`${classes.searchBox}${
          compactToolbars ? ` ${classes.searchBoxCompact}` : ''
        }`}
        data-cy="atomMapSearch"
        ref={setAnchorEl}
      >
        <SearchAutocomplete
          search={search}
          anchorEl={anchorEl}
          startAdornment={startAdornment}
        />
        <MapEditList
          editing={editing}
          open={!isEditing && open}
          anchorEl={anchorEl}
        />
      </div>
      {isEditing && (
        <div className={classes.editActions} data-cy="mapEditActions">
          {editing.editMode === 'linestring' && (
            <IconButton
              size="small"
              tooltip="Clear"
              data-cy="mapEditClear"
              disabled={!editing.hasDraft}
              onClick={editing.onClear}
            >
              <Icon icon={DeleteIcon} />
            </IconButton>
          )}
          <IconButton
            size="small"
            tooltip="Cancel"
            data-cy="mapEditCancel"
            onClick={editing.onCancel}
          >
            <Icon icon={CloseIcon} />
          </IconButton>
          <IconButton
            size="small"
            tooltip="Save"
            data-cy="mapEditSave"
            disabled={!editing.hasDraft}
            onClick={editing.onSave}
          >
            <Icon className={classes.saveIcon} icon={CheckIcon} />
          </IconButton>
        </div>
      )}
    </>
  );
};

export default EditableSearchBox;
